import {
  ClientCredential,
  ClientCredentialInput,
  createClientCredential,
  listClientCredentials,
  updateClientCredential,
} from '../../api/clientCredentialApi'
import NavBar from '../../components/nav-bar/nav-bar'
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  notification,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'

const roleOptions = [
  { label: '内部客户端', value: 'ROLE_CLIENT_INTERNAL' },
  { label: '对接方', value: 'ROLE_CLIENT_INTEGRATOR' },
  { label: '企业客户', value: 'ROLE_CLIENT_CORPORATE' },
  { label: '权益平台', value: 'ROLE_CLIENT_BENEFIT_PLATFORM' },
]

const ClientCredentialsPage = () => {
  const [credentials, setCredentials] = useState<ClientCredential[]>([])
  const [editor, setEditor] = useState<ClientCredential | 'new' | null>(null)
  const [filters, setFilters] = useState<{
    clientId?: string
    name?: string
    role?: string
    isBlocked?: boolean
  }>({})
  const [form] = Form.useForm<ClientCredentialInput>()
  const [filterForm] = Form.useForm<typeof filters>()
  const [loading, setLoading] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
      )
      setCredentials(await listClientCredentials(params))
    } catch (error: any) {
      notification.error({ message: '客户端凭据加载失败', description: error?.message })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    reload()
  }, [reload])

  const openEditor = (credential: ClientCredential | 'new') => {
    form.resetFields()
    form.setFieldsValue(credential === 'new' ? { isBlocked: false } : credential)
    setEditor(credential)
  }

  const save = async () => {
    const values = await form.validateFields()
    try {
      if (editor === 'new') {
        await createClientCredential(values)
      } else if (editor) {
        await updateClientCredential(editor.id, values)
      }
      setEditor(null)
      await reload()
      notification.success({ message: '客户端凭据已保存' })
    } catch (error: any) {
      notification.error({ message: '客户端凭据保存失败', description: error?.message })
    }
  }

  return (
    <Space className="dashboard-wrapper" orientation="vertical">
      <NavBar title="客户端凭据管理" />
      <Card>
        <Form form={filterForm} layout="inline" onFinish={setFilters}>
          <Form.Item name="clientId" label="客户端 ID">
            <Input allowClear />
          </Form.Item>
          <Form.Item name="name" label="名称">
            <Input allowClear />
          </Form.Item>
          <Form.Item name="role" label="角色">
            <Select allowClear options={roleOptions} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="isBlocked" label="状态">
            <Select
              allowClear
              style={{ width: 110 }}
              options={[
                { label: '启用', value: false },
                { label: '停用', value: true },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit">筛选</Button>
          </Form.Item>
        </Form>
        <Button type="primary" onClick={() => openEditor('new')}>
          新增客户端凭据
        </Button>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={credentials}
          pagination={{ pageSize: 20 }}
          columns={[
            { title: 'ID', dataIndex: 'id' },
            { title: '名称', dataIndex: 'name' },
            { title: '客户端 ID', dataIndex: 'clientId' },
            {
              title: '角色',
              dataIndex: 'role',
              render: (role: string) =>
                roleOptions.find(option => option.value === role)?.label ?? role,
            },
            { title: '创建时间', dataIndex: 'createdAt' },
            {
              title: '状态',
              dataIndex: 'isBlocked',
              render: (blocked: boolean) => (
                <Tag color={blocked ? 'default' : 'green'}>{blocked ? '停用' : '启用'}</Tag>
              ),
            },
            {
              title: '操作',
              render: (_: unknown, credential: ClientCredential) => (
                <Button onClick={() => openEditor(credential)}>编辑</Button>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editor === 'new' ? '新增客户端凭据' : '编辑客户端凭据'}
        open={editor !== null}
        onCancel={() => setEditor(null)}
        onOk={save}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="clientId" label="Client ID" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="clientSecret"
            label="Client Secret"
            rules={editor === 'new' ? [{ required: true }] : []}
            extra={editor === 'new' ? undefined : '留空则保持原密钥不变'}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="name" label="客户端名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select
              options={roleOptions.map(option => ({
                ...option,
                label: `${option.label}（${option.value}）`,
              }))}
            />
          </Form.Item>
          <Form.Item name="isBlocked" label="停用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}

export default ClientCredentialsPage
